// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { OrderPaymentChannel, OrdersPaymentStatus, OrdersStatus } from '@/models/orders.model';

// Components
import { StyledTitleModal, StyledSelect, StyledButton } from './UpdateOrderPaymentModalStyles';
import { Modal, ModalSize } from '@/components';

// Helpers
import { paymentEditStatuses, getPaymentChannelOptions, getPaymentStatusOptions } from '../../helpers';

interface UpdateOrderPaymentModalProps {
  isOpenUpdateOrderPaymentModal: boolean;
  isLoading: boolean;
  currentPaymentStatus: OrdersPaymentStatus;
  currentPaymentChannel?: OrderPaymentChannel | null;
  orderStatus: OrdersStatus;
  closeUpdateOrderPaymentModal: () => void;
  handleUpdatePayment: (paymentStatus?: OrdersPaymentStatus, paymentChannel?: OrderPaymentChannel) => void;
}

const UpdateOrderPaymentModal = ({
  isOpenUpdateOrderPaymentModal,
  isLoading,
  currentPaymentStatus,
  currentPaymentChannel,
  orderStatus,
  closeUpdateOrderPaymentModal,
  handleUpdatePayment,
}: UpdateOrderPaymentModalProps) => {
  const { t } = useTranslation(['orders']);
  const paymentStatusOptions = getPaymentStatusOptions(t);
  const paymentChannelOptions = getPaymentChannelOptions(t);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(
    paymentStatusOptions.find((el) => el.value === currentPaymentStatus)?.value,
  );
  const [selectedPaymentChannel, setSelectedPaymentChannel] = useState<OrderPaymentChannel | undefined>(
    currentPaymentChannel ?? undefined,
  );
  const isStatusChangeEnabled = paymentEditStatuses?.has(orderStatus);

  useEffect(() => {
    if (isOpenUpdateOrderPaymentModal) {
      setSelectedPaymentStatus(paymentStatusOptions.find((el) => el.value === currentPaymentStatus)?.value);
      setSelectedPaymentChannel(currentPaymentChannel ?? undefined);
    }
  }, [isOpenUpdateOrderPaymentModal, currentPaymentStatus, currentPaymentChannel]);

  const onPaymentStatusChange = (value: unknown) => {
    setSelectedPaymentStatus(value as OrdersPaymentStatus);
  };

  const onPaymentChannelChange = (value: unknown) => {
    setSelectedPaymentChannel(value as OrderPaymentChannel);
  };

  return (
    <Modal visible={isOpenUpdateOrderPaymentModal} onCancel={closeUpdateOrderPaymentModal} size={ModalSize.LARGE}>
      <StyledTitleModal>{t('orders_list_payment_status')}</StyledTitleModal>
      <StyledSelect
        options={paymentStatusOptions}
        placeholder={t('orders_status_select_placeholder')}
        onChange={onPaymentStatusChange}
        value={selectedPaymentStatus}
        size="large"
        allowClear
      />
      <StyledTitleModal>{t('orders_list_payment_channel')}</StyledTitleModal>
      <StyledSelect
        options={paymentChannelOptions}
        placeholder={t('orders_status_select_placeholder')}
        onChange={onPaymentChannelChange}
        value={selectedPaymentChannel}
        size="large"
      />
      <StyledButton
        onClick={() => handleUpdatePayment(selectedPaymentStatus, selectedPaymentChannel)}
        disabled={isLoading || !isStatusChangeEnabled || !selectedPaymentStatus}
        type="primary"
      >
        {t('update_order_payment_modal_button')}
      </StyledButton>
    </Modal>
  );
};

export default UpdateOrderPaymentModal;
